import { ReactNode } from "react"
import { HealthCheckGroup, ProjectHealth, ProjectHealthHistoryItem, ProjectHealthUI } from "models/project-health"
import HealthHistory from "Components/molecule/health-history"
import HealthCheckCard from "Components/molecule/cards/health-check-card"
import {
    ProjectCumulativeRevenueChart, ProjectHoursChart, ProjectMonthlyRevenueChart,
} from "Components/molecule/charts/project-cashflow-chart"
import LoadingState from "Components/organisms/loading-state"
import EmptyState from "Components/organisms/empty-state"
import InfoTip from "Components/atoms/info-tip"
import HealthKeyFigures from "Components/molecule/health-key-figures"
import { formatCurrency, formatNumber } from "utils/tools"

interface ProjectHealthPanelProps {
    health: ProjectHealth | null
    history: ProjectHealthHistoryItem[]
    isLoading: boolean
}

const money = (v: number | null | undefined) => formatCurrency(v ?? 0, "CAD", 0)
const dateLabel = (v: string | null) => (v ? v.split("T")[0] : "—")

// What each section is and where its data comes from (info icon next to the title)
const INFO = {
    history: "Overall status frozen at each month close (monthly snapshots), plus the live status of the current month. Hover a month to see which checks failed.",
    kpis: "Life-to-date values from the Acumatica GL transactions synced to IbexIQ: revenue = billed (410000/420000) + accrued (410100/410200); cost = accounts 500001–699999 incl. timecards (510000). Budgets are the Income / Expense budget lines of the project in Acumatica.",
    setup: "ToR Rev A setup checks. Planned Spend and Forecast use the monthly plan/forecast entered in Financials & Forecasting. Contract Executed and Escalation Clause show 'Not evaluated' because those fields are not synced from Acumatica yet.",
    financial: "ToR Rev A financial checks. Gross Margin uses the current fiscal year's bands from Admin > Project Margin Targets. AR Aging needs invoices, which are not synced from Acumatica yet. Unbilled is approximated from GL periods.",
    monthly: "Per month: Planned and Forecast come from Financials & Forecasting (ProjectFinance); Actual comes from the GL revenue accounts (410000, 420000, 410100, 410200) for closed months.",
    cumulative: "Running total: actuals up to last month, then the forecast for the current and future months, compared to the Income budget line (dashed). Crossing the line means the project will exceed its budget.",
    indicators: "Context only. These do not change the Project Health status.",
    schedule: "Project start/end dates and task % complete come from Acumatica (project and project tasks). Time elapsed is today compared to the project dates.",
    hours: "Hours charged to the project from Acumatica timesheets (Activity), per month: total hours and the billable part.",
}

const Title = ({ id, children, info }: { id: string, children: ReactNode, info: ReactNode }) => (
    <h5 className="section-title">{children} <InfoTip id={`ph-${id}`} text={info} /></h5>
)

const ProjectHealthPanel = ({ health, history, isLoading }: ProjectHealthPanelProps) => {
    if (isLoading)
        return <LoadingState title="Loading Project Health" subtitle="Checking margins, billing and forecast..." />

    if (!health)
        return <EmptyState title="Project health unavailable" subtitle="The health data could not be loaded." />

    const { kpis, indicators } = health
    const setupChecks = health.checks.filter(c => c.group === HealthCheckGroup.SETUP)
    const financialChecks = health.checks.filter(c => c.group === HealthCheckGroup.FINANCIAL)
    const taskBehind = indicators.elapsedPercent !== null && indicators.averageTaskCompletion !== null
        && indicators.averageTaskCompletion + 10 < indicators.elapsedPercent

    return (
        <div className="project-health">
            <span className="muted">
                Rules: ToR Rev A (worst result wins) · Current period: {ProjectHealthUI.periodLabel(health.currentPeriod)}
                {!health.evaluated && ` · Status is ${health.projectStatus ?? "unknown"}, checks below are for reference only`}
            </span>

            {/* Monthly status trail (snapshots) */}
            {health.evaluated && (
                <div>
                    <Title id="history" info={INFO.history}>Status by Month</Title>
                    <HealthHistory
                        history={history}
                        currentPeriod={health.currentPeriod}
                        currentStatus={health.status}
                        currentFailing={health.failingChecks}
                    />
                </div>
            )}

            {/* KPIs (GL actuals, life to date): 2 rows of 4 */}
            <div>
                <Title id="kpis" info={INFO.kpis}>Key Figures</Title>
                <HealthKeyFigures health={health} />
            </div>

            {/* ToR checks */}
            <div className="health-checks">
                <div>
                    <Title id="setup" info={INFO.setup}>Project Setup</Title>
                    <div className="health-check-list">
                        {setupChecks.map(c => <HealthCheckCard key={c.key} check={c} />)}
                    </div>
                </div>
                <div>
                    <Title id="financial" info={INFO.financial}>Financial Benchmarks</Title>
                    <div className="health-check-list">
                        {financialChecks.map(c => <HealthCheckCard key={c.key} check={c} />)}
                    </div>
                </div>
            </div>

            {/* Cashflow */}
            {health.series.length > 0 ? (
                <div className="health-charts">
                    <div className="chart-box">
                        <Title id="monthly" info={INFO.monthly}>Monthly Revenue</Title>
                        <ProjectMonthlyRevenueChart series={health.series} currentPeriod={health.currentPeriod} />
                    </div>
                    <div className="chart-box">
                        <Title id="cumulative" info={INFO.cumulative}>Cumulative Revenue vs Budget</Title>
                        <ProjectCumulativeRevenueChart series={health.series} currentPeriod={health.currentPeriod} budget={kpis.incomeBudget} />
                    </div>
                </div>
            ) : (
                <EmptyState title="No financial activity yet" subtitle="There are no GL postings or forecast rows for this project." />
            )}

            {/* Informational indicators (do not affect the status) */}
            <div>
                <Title id="indicators" info={INFO.indicators}>
                    Indicators <span className="muted">(informational, not part of the status)</span>
                </Title>
                <div className="health-indicators">
                    <div className="indicator">
                        <strong>Schedule <InfoTip id="ph-schedule" text={INFO.schedule} /></strong>
                        <span className="muted">{dateLabel(indicators.startDate)} → {dateLabel(indicators.endDate)}</span>
                        <div className="bar-row">
                            <small>Time elapsed: {indicators.elapsedPercent !== null ? `${formatNumber(indicators.elapsedPercent, 0)}%` : "—"}</small>
                            <div className="progress">
                                <div className="progress-bar bg-secondary" style={{ width: `${indicators.elapsedPercent ?? 0}%` }} />
                            </div>
                        </div>
                        <div className="bar-row">
                            <small>Average task completion: {indicators.averageTaskCompletion !== null ? `${formatNumber(indicators.averageTaskCompletion, 0)}%` : "—"}</small>
                            <div className="progress">
                                <div className={`progress-bar ${taskBehind ? "bg-warning" : "bg-success"}`} style={{ width: `${indicators.averageTaskCompletion ?? 0}%` }} />
                            </div>
                        </div>
                        {indicators.isPastEndDate && health.evaluated && (
                            <small className="text-danger"><i className="mdi mdi-calendar-alert me-1" />End date has passed but the project is still Active.</small>
                        )}
                        {taskBehind && (
                            <small className="text-warning"><i className="mdi mdi-alert me-1" />Task completion is behind the time elapsed.</small>
                        )}
                        <small className="muted">{indicators.overdueTasks} of {indicators.totalTasks} task(s) past their end date and not completed.</small>
                    </div>
                    <div className="indicator">
                        <strong>Hours <InfoTip id="ph-hours" text={INFO.hours} /></strong>
                        <span className="muted">
                            {formatNumber(indicators.totalHours, 0)} h total · {formatNumber(indicators.totalBillableHours, 0)} h billable
                            {indicators.totalHours > 0 ? ` (${formatNumber((indicators.totalBillableHours / indicators.totalHours) * 100, 0)}%)` : ""}
                        </span>
                        <ProjectHoursChart series={health.series} />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ProjectHealthPanel
